import React, { useState, useEffect } from 'react'
import { t } from 'i18next'
import { Divider } from 'components/divider'
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/20/solid'
import { useNavigate, useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { GET_VESSEL_CHECKLIST_SUBMISSION_DETAILS } from 'graphql/operations/queries'
import loadingImage from '../../../../assets/images/boat-loading.gif'
import { customDateFormat } from 'utils/common'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import { GraphQlResponseNoData } from 'components/custom/GraphQlResponseDisplay'

export default function CheckListDetails() {
    const { id, submission_date } = useParams()
    console.log('Submission ID: ', id)
    const [checkLists, setCheckLists] = useState(null)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [openAccordions, setOpenAccordions] = useState([])

    const navigate = useNavigate()
    const redirectDetailPage = () => {
        navigate('/vessel/checklist')
    }

    const [getCheckListDetails, { loading: checkListLoading, error: checkListError }] = useLazyQuery(
        GET_VESSEL_CHECKLIST_SUBMISSION_DETAILS,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getVesselChecklistSubmission.success && data.getVesselChecklistSubmission.code === '200') {
                    setCheckLists(data.getVesselChecklistSubmission.submissions[0])
                    setOpenAccordions(
                        new Array(data.getVesselChecklistSubmission.submissions[0].checklist_category.length).fill(true)
                    )
                }
            },
        }
    )

    useEffect(() => {
        getCheckListDetails({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                toDate: submission_date.split('T')[0],
                fromDate: submission_date.split('T')[0],
                boatId: null,
                captainId: null,
                submissionId: id,
            },
        })
    }, [id])

    const toggleAccordion = index => {
        setOpenAccordions(prevState => prevState.map((isOpen, i) => (i === index ? !isOpen : isOpen)))
    }

    const engineRows = [
        {
            label: t('Boat Port Engine Hours (P):'),
            value: checkLists?.boat_engine_log?.port_engine_hours,
            bg: 'bg-white',
        },
        {
            label: t('Port Center Engine Hours (P/c):'),
            value: checkLists?.boat_engine_log?.port_center_engine_hours,
            bg: 'bg-gray-50',
        },
        {
            label: t('Starboard Center Engine Hours (S/c):'),
            value: checkLists?.boat_engine_log?.starboard_center_engine_hours,
            bg: 'bg-white',
        },
        {
            label: t('Starboard Engine Hours (S):'),
            value: checkLists?.boat_engine_log.starboard_engine_hours,
            bg: 'bg-gray-50',
        },
        {
            label: t('Generator'),
            value: checkLists?.boat_engine_log.generator_hours,
            bg: 'bg-white',
        },
        {
            label: t('Fuel'),
            value:
                checkLists?.boat_engine_log.fuel_level_percent != null
                    ? `${checkLists?.boat_engine_log.fuel_level_percent}%`
                    : null,
            bg: 'bg-gray-50',
        },
        {
            label: t('Diesel'),
            value:
                checkLists?.boat_engine_log?.diesel_level_percent != null
                    ? `${checkLists.boat_engine_log.diesel_level_percent}%`
                    : null,
            bg: 'bg-white',
        },
    ]

    const validEngineRows = engineRows.filter(row => row.value != null && row.value !== '')

    return (
        <>
            <div className="mb-6 flex justify-normal items-center">
                <button
                    onClick={redirectDetailPage}
                    type="button"
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <h3 className="text-lg font-bold leading-7 text-primary ps-2.5 pb-1"> {t('Checklist details')}</h3>
            </div>
            <div className="flex flex-wrap items-end justify-between pb-0">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="mt-4">
                        <div className="px-4 sm:px-0">
                            <h2 className="text-base font-semibold leading-7 text-primary">{t('Boat Information')}</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Information about the boat and the captain who submitted this checklist.')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {checkListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait')}...</p>
                </div>
            )}
            {checkListError && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('Failed to fetch records')}.</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('Please contact the administrator or try again later.')}
                    </p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!checkListLoading && !checkListError && checkLists == null && <GraphQlResponseNoData />}
            {!checkListLoading && !checkListError && checkLists != null && (
                <>
                    <div className="flex flex-wrap items-end justify-between gap-4 pb-10">
                        <div className="max-sm:w-full sm:flex-1">
                            <div className="mt-4 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {t('Boat Name')}
                                        </dd>
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {checkLists.vessel.boat.name.en}
                                        </dt>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {t('Boat Registration Number')}
                                        </dd>
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {checkLists.vessel.boat.details.reg_number}
                                        </dt>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <div className="max-sm:w-full sm:flex-1">
                            <div className="mt-4">
                                <div className="mt-3 border-t border-gray-100">
                                    <dl className="divide-y divide-gray-100">
                                        <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {t('Submitted On')}
                                            </dd>
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {customDateFormat(checkLists.submission_date)}
                                            </dt>
                                        </div>
                                        <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {t('Submitted By')}
                                            </dd>
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {checkLists.captain?.user_info?.given_name}{' '}
                                                {checkLists.captain?.user_info?.family_name}
                                            </dt>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Divider />
                    {validEngineRows.length > 0 && (
                    <>
                    <div className="flex flex-wrap items-end justify-between pb-0">
                        <div className="max-sm:w-full sm:flex-1">
                            <div className="mt-4">
                                <div className="px-4 sm:px-0">
                                    <h2 className="text-base font-semibold leading-7 text-primary">
                                        {t('Boat Engine Log')}
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-gray-600">
                                        {t('Details of backlog provided.')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                        <div className="flex flex-wrap items-end justify-between gap-4 pb-10">
                            <div className="max-sm:w-full sm:flex-1">
                                <div className="mt-4 border-t border-gray-100">
                                    <dl className="divide-y divide-gray-100">
                                        {validEngineRows.map((row, index) => (
                                            <div
                                                key={index}
                                                className={`${row.bg} px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3`}
                                            >
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {row.label}
                                                </dd>
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {row.value}
                                                </dt>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </div>
                        </div>
                        </>
                    )}
                    <Divider />
                    <div className="space-y-10 divide-y divide-gray-900/10 mt-10 mb-0">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3 pb-6">
                            <div className="px-4 sm:px-0">
                                <h2 className="text-base font-semibold leading-7 text-primary">
                                    {t('Checklist Information')}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Information about the checklist submitted by the captains.')}
                                </p>
                            </div>

                            <div className="col-span-2">
                                <div>
                                    <div className="pb-0">
                                        {checkLists.checklist_category.map((category, index) => (
                                            <div key={category.category_id}>
                                                <div className="w-full border border-gray-200 rounded shadow-sm mb-4">
                                                    <div
                                                        onClick={() => toggleAccordion(index)}
                                                        className="flex text-md justify-between items-center bg-blue-500 text-primary px-4 py-2 font-semibold cursor-pointer"
                                                    >
                                                        <span>{category.description.en}</span>
                                                        <span>
                                                            {openAccordions[index] ? (
                                                                <svg
                                                                    className="w-4 h-4"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M20 12H4"
                                                                    ></path>
                                                                </svg>
                                                            ) : (
                                                                <svg
                                                                    className="w-4 h-4"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M12 4v16m8-8H4"
                                                                    ></path>
                                                                </svg>
                                                            )}
                                                        </span>
                                                    </div>
                                                    {openAccordions[index] && (
                                                        <div className="bg-white px-4 py-3">
                                                            <div className="w-full p-2 pt-0">
                                                                {category.checklist_entries.map((entries, index) => (
                                                                    <div
                                                                        key={entries.entry_id}
                                                                        className={`flex justify-between items-center py-3 ${index !== category.checklist_entries.length - 1 ? 'border-b' : ''}`}
                                                                    >
                                                                        <span className="text-gray-700">
                                                                            {entries.checklist.item_name.en}
                                                                        </span>
                                                                        {entries.item_status.is_checked && (
                                                                            <CheckIcon className="h-5 w-5 text-green" />
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {checkLists.additional_info?.remarks && (
                                        <div className="border-t border-gray-100 px-4 py-6 sm:col-span-2 sm:px-0">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Remarks')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-2">
                                                {checkLists.additional_info?.remarks}
                                            </dd>
                                        </div>
                                    )}
                                    {checkLists.additional_info?.attachments.length > 0 && (
                                        <div className="border-t border-gray-100 px-4 py-6 sm:col-span-2 sm:px-0">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Attachments')}
                                            </dt>
                                            <ul
                                                role="list"
                                                className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8"
                                            >
                                                {checkLists.additional_info?.attachments.map((file, index) => (
                                                    <li key={file?.file_name} className="relative mt-3">
                                                        <div className="group h-[150px] aspect-w-5 block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100">
                                                            <ImageDisplay
                                                                s3Key={file.s3_key}
                                                                file={file.file_data}
                                                                classStyle="object-cover group-hover:opacity-75 w-full"
                                                            />
                                                            <button
                                                                type="button"
                                                                className="absolute inset-0 focus:outline-none"
                                                            >
                                                                <span className="sr-only">
                                                                    {t('View details for')} {file?.file_name}
                                                                </span>
                                                            </button>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <Divider />
                </>
            )}
        </>
    )
}
